import React, { useState, useRef, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import type { TurnMarkerData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useConversation } from '../../../../contexts/ConversationContext';
import { useDagNodeOpacity, useChatDagRuntime } from '../../../../contexts/ChatDagRuntimeContext';
import HoverCard from '../../../common/HoverCard';
import DagNodeHoverContent from './DagNodeHoverContent';
import { MarkdownPreview } from '../MarkdownPreview';
import { CopyChip } from '../CopyChip';
import SquadLaunchPopover from '../SquadLaunchPopover';
import {
  PIP_HEADER_CLASS,
  HOVER_LIFT_CLASS,
  SELECTION_RING_CLASS,
} from '../chatDagTokens';

// Anchor for the start/end of a turn.
//
// UX triad review (v0.2): the DIFFERENTIATING field is the content
// preview — every user marker has the same "👤 You" but different
// words. Render the preview as the dominant text; push the role label
// to a small pip so the eye lands on signal, not chrome.
//
// Assistant markers surface a compact telemetry chip row
// (🧰 · 💰 · tokens) pulled from `message.metadata.turn_telemetry`.

function formatCost(usd: number | null | undefined): string {
  if (usd == null) return '';
  if (usd === 0) return '$0';
  if (usd < 0.001) return `$${usd.toFixed(5)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

// T3 — cost tier color so an expensive turn FEELS expensive on the
// asst marker: amber over 5¢, rose over $1. Returns '' for the
// default opacity-80 inherited tone.
function costTierClass(usd: number | null | undefined, darkMode: boolean): string {
  if (usd == null) return '';
  if (usd >= 1) return darkMode ? 'text-rose-400 font-semibold' : 'text-rose-600 font-semibold';
  if (usd >= 0.05) return darkMode ? 'text-amber-300' : 'text-amber-600';
  return '';
}

function formatTime(ts?: string): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const TurnMarkerNodeCompact: React.FC<{ data: TurnMarkerData; selected?: boolean }> = ({ data, selected }) => {
  const { darkMode } = useTheme();
  const { focusBubble, openInvitePeer, canInvitePeer, openReconsider, canReconsider, toggleArtifactsExpanded } = usePersonaChatUI();
  const conversation = useConversation();
  const opacityStyle = useDagNodeOpacity(data.turnKey, false);
  // Trailmap L2 Drip 2 — in grouped mode the cluster IS the disclosure, so the
  // artifact badge demotes from a toggle button to a passive count chip.
  const { activeDim } = useChatDagRuntime();
  const grouped = activeDim !== 'off';
  const isUser = data.role === 'user';
  const messageId = data.messageId;
  const [showLaunchPopover, setShowLaunchPopover] = useState(false);
  const launchBtnRef = useRef<HTMLButtonElement>(null);

  const handleLaunchClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowLaunchPopover((v) => !v);
  }, []);

  // v0.1 Item 8 — phantom user_markers (synthesized by the processor
  // for orphan asst chains so layoutChatDag positions them as proper
  // turns) render as a thin invisible passthrough so they participate
  // in layout + edge routing without visually polluting the DAG.
  if ((data as any).isPhantom) {
    return (
      <div
        data-testid="turn-marker-phantom"
        style={{ width: 1, height: 1, opacity: 0 }}
      >
        <Handle type="target" position={Position.Top} id="t" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
      </div>
    );
  }
  const onClick = messageId
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        focusBubble(messageId);
      }
    : undefined;

  // User markers: blue pennant reading "incoming intent."
  // Assistant markers: emerald keystone "turn closes here."
  const accent = isUser
    ? darkMode
      ? 'bg-blue-600/25 border-blue-500/70 text-blue-50'
      : 'bg-blue-50 border-blue-400 text-blue-900'
    : darkMode
      ? 'bg-emerald-700/25 border-emerald-500/70 text-emerald-50'
      : 'bg-emerald-50 border-emerald-500 text-emerald-900';

  const pipStyle = isUser
    ? darkMode
      ? 'text-blue-300/70'
      : 'text-blue-600'
    : darkMode
      ? 'text-emerald-300/70'
      : 'text-emerald-600';

  // Trailmap L2 Drip 1 — artifact-count badge. Toggles this turn's tile
  // disclosure (default-collapsed). stopPropagation so the card's own
  // onClick (scroll-to-bubble) doesn't also fire.
  const artifactBadge = data.artifactCount ? (
    grouped ? (
      // Drip 2: passive count chip — the cluster IS the disclosure, so the badge
      // is a truthful read-only count, not a toggle (no lie-by-sameness).
      <span
        data-testid={`artifact-badge-${messageId}`}
        className={`font-mono text-[10px] inline-flex items-center gap-0.5 rounded px-1 ${
          darkMode ? 'text-emerald-300' : 'text-emerald-700'
        }`}
      >
        ▣ {data.artifactCount}
      </span>
    ) : (
      <button
        type="button"
        data-testid={`artifact-badge-${messageId}`}
        aria-expanded={!!data.artifactsExpanded}
        onClick={(e) => {
          e.stopPropagation();
          if (data.artifactTurnKey) toggleArtifactsExpanded(data.artifactTurnKey);
        }}
        title={data.artifactsExpanded ? 'Hide artifacts' : 'Show artifacts'}
        className={`font-mono text-[10px] inline-flex items-center gap-0.5 rounded px-1 transition-colors ${
          darkMode
            ? 'text-emerald-300 hover:bg-emerald-800/40'
            : 'text-emerald-700 hover:bg-emerald-100'
        }`}
      >
        ▣ {data.artifactCount} <span aria-hidden="true">{data.artifactsExpanded ? '▾' : '▸'}</span>
      </button>
    )
  ) : null;

  return (
    <HoverCard
      maxWidth={360}
      enterDelay={250}
      content={<DagNodeHoverContent data={data} />}
    >
    <div
      className={`group relative px-3 py-2 rounded-md border text-xs w-[260px] shadow-sm ${HOVER_LIFT_CLASS} ${accent} ${
        selected ? SELECTION_RING_CLASS : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      title={onClick ? `${data.preview}\n\nClick to scroll to bubble` : data.preview}
      // Soft visual distinction per role — asymmetric corner
      // rounding, so a user marker points "down into" the flow and
      // an assistant marker caps "up from" the fan-in.
      // Dim non-viewport turns per the spec rev 1.1 § 9 priority rules.
      style={{
        ...(isUser
          ? { borderTopLeftRadius: 12, borderBottomRightRadius: 12 }
          : { borderTopRightRadius: 12, borderBottomLeftRadius: 12 }),
        ...opacityStyle,
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!opacity-0 !pointer-events-none"
      />

      {(data.fullContent || data.preview) && (
        <CopyChip
          text={data.fullContent || data.preview}
          label={isUser ? 'Copy prompt' : 'Copy answer'}
        />
      )}

      {/* Tiny role + time pip — not the main event */}
      <div className={`flex items-center gap-1.5 ${PIP_HEADER_CLASS} ${pipStyle}`}>
        <span>{isUser ? '👤 You' : '🤖 Assistant'}</span>
        {data.timestamp && <span className="opacity-60 font-mono normal-case tracking-normal">{formatTime(data.timestamp)}</span>}
      </div>

      {/* User-marker fallback (mid-turn, no assistant marker yet) — no
          telemetry row here, so the badge rides under the role pip. */}
      {isUser && data.artifactCount ? <div className="mt-1">{artifactBadge}</div> : null}

      {/* Preview — the load-bearing content of this node.
          Asst markers carry LLM markdown source; render through
          MarkdownPreview so **bold**, lists, inline code etc. read
          as styled text instead of raw asterisks. User markers stay
          plain — typed prose rarely contains markdown. */}
      <div className="mt-1 font-medium leading-snug">
        {data.preview ? (
          isUser ? (
            <div className="line-clamp-3 whitespace-pre-wrap break-words">
              {data.preview}
            </div>
          ) : (
            <MarkdownPreview
              text={data.preview}
              maxLines={3}
              textSizeClass="text-xs"
            />
          )
        ) : (
          <span className="opacity-50 italic font-normal">(empty turn)</span>
        )}
      </div>

      {/* Assistant-only telemetry chips */}
      {!isUser && (data.totalCostUsd != null || data.toolCallCount || data.totalTokens != null || data.artifactCount) ? (
        <div className="mt-1.5 flex items-center gap-2.5 text-[10px] font-mono opacity-80 pt-1.5 border-t border-current/10">
          {data.toolCallCount ? <span title="Tool invocations this turn">🧰 {data.toolCallCount}</span> : null}
          {data.totalCostUsd != null ? <span title="Estimated cost" className={costTierClass(data.totalCostUsd, darkMode)}>💰 {formatCost(data.totalCostUsd)}</span> : null}
          {data.totalTokens != null ? <span title="Total tokens">{data.totalTokens.toLocaleString()} tok</span> : null}
          {artifactBadge}
        </div>
      ) : null}

      {!isUser && messageId && (
        <button
          ref={launchBtnRef}
          onClick={handleLaunchClick}
          className={`absolute -right-1 -top-1 w-5 h-5 rounded-full text-[10px]
            opacity-0 group-hover:opacity-100 transition-opacity shadow-sm
            flex items-center justify-center ${
            darkMode
              ? 'bg-indigo-600 text-white hover:bg-indigo-500'
              : 'bg-indigo-500 text-white hover:bg-indigo-600'
          }`}
          title="Launch squad from this turn"
        >
          ⚡
        </button>
      )}
      {!isUser && messageId && canInvitePeer && (
        <button
          type="button"
          data-testid={`invite-peer-node-${messageId}`}
          onClick={(e) => { e.stopPropagation(); openInvitePeer(messageId); }}
          className={`absolute -left-1 -top-1 w-5 h-5 rounded-full text-[10px]
            opacity-0 group-hover:opacity-100 transition-opacity shadow-sm
            flex items-center justify-center ${
            darkMode
              ? 'bg-teal-600 text-white hover:bg-teal-500'
              : 'bg-teal-500 text-white hover:bg-teal-600'
          }`}
          title="Invite a peer onto the trail at this turn"
        >
          ⊕
        </button>
      )}
      {/* C2.3 — ↻ reconsider action (B-T10): non-head = active orange (spawn a
          companion to re-examine this earlier turn); head = disabled greyed (a
          POSITIVE gate signal, not invisible-by-absence). Below ⊕ on the left rail. */}
      {!isUser && messageId && canReconsider && (
        <button
          type="button"
          data-testid={`reconsider-node-${messageId}`}
          disabled={data.isLatestTurn}
          onClick={data.isLatestTurn ? undefined : (e) => { e.stopPropagation(); openReconsider(messageId); }}
          className={`absolute -left-1 top-5 w-5 h-5 rounded-full text-[10px]
            opacity-0 transition-opacity shadow-sm flex items-center justify-center ${
            data.isLatestTurn
              ? `group-hover:opacity-40 cursor-not-allowed ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'}`
              : `group-hover:opacity-100 ${darkMode ? 'bg-orange-600 text-white hover:bg-orange-500' : 'bg-orange-500 text-white hover:bg-orange-600'}`
          }`}
          title={data.isLatestTurn
            ? 'Reconsider applies to earlier turns — fold a companion forward here instead'
            : 'Reconsider this earlier turn — spawn a companion to re-examine it'}
        >
          ↻
        </button>
      )}
      {/* C2.3 — ⤺ retrace STATE badge (D-T8): bottom-right, off the left action
          rail (⊕/↻), persistent. Marks a turn a switchback re-blazed from. */}
      {!isUser && messageId && (data as any).reconsideredOrigin && (
        <span
          data-testid={`retrace-marker-${messageId}`}
          title="A reconsideration re-blazed the trail from this turn"
          className={`absolute -right-1 -bottom-1 px-1 rounded text-[10px] shadow-sm pointer-events-none ${
            darkMode ? 'bg-orange-900/70 text-orange-200' : 'bg-orange-100 text-orange-700'
          }`}
        >
          ⤺ retraced
        </span>
      )}
      {showLaunchPopover && messageId && (
        <SquadLaunchPopover
          sessionId={conversation.state.id}
          anchorMessageId={messageId}
          anchorLabel={data.preview?.slice(0, 40) ?? 'this turn'}
          triggerRect={launchBtnRef.current?.getBoundingClientRect() ?? new DOMRect()}
          onClose={() => setShowLaunchPopover(false)}
        />
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        id="b"
        className="!opacity-0 !pointer-events-none"
      />
      {/* Trailmap L1+ (2026-05-12) — right-side source handle for
          artifact_tile fan-out. Edges from this turn-marker to tile
          nodes use sourceHandle: 'r' + targetHandle: 'l' so the visual
          edge enters the tile from its left + exits the turn-marker
          from its right (instead of the default bottom). */}
      <Handle
        type="source"
        position={Position.Right}
        id="r"
        className="!opacity-0 !pointer-events-none"
      />
    </div>
    </HoverCard>
  );
};

export default TurnMarkerNodeCompact;
